import React from 'react';

import { COLORS, ANIM } from '../../theme/tokens';
import {
  Wrap,
  Svg,
  Tooltip,
  TipDay,
  TipRows,
  TipRow,
  TipSwatch,
  TipName,
  TipValue,
} from './LineChart.styles';

export type ChartSeries = {
  name: string;
  /** Stroke colour — supply a token value (e.g. `PULSE.chartLine`), never a literal. */
  color: string;
  points: number[];
  dashed?: boolean;
};

export type LineChartProps = {
  series: ChartSeries[];
  /** X-axis labels. Pass `[]` to drop the axis row entirely (Pulse hero look). */
  xLabels: string[];
  /**
   * Per-point labels for the tooltip heading — one per data sample. Use this
   * (instead of `xLabels`) when the chart draws no visible axis but the hover
   * readout should still name the point: the Pulse hero's monthly $ trend keeps
   * `xLabels={[]}` for the clean line yet feeds the tooltip its month here.
   * Indexed directly by the hovered sample, so it takes precedence over the
   * proportional `xLabels` mapping when set.
   */
  pointLabels?: string[];
  height?: number;
  yTicks?: number;
  /** Draw the Y gridlines + tick labels on the left. */
  showY?: boolean;
  formatY?: (n: number) => string;
  /** Fill a soft gradient area under the first series (Pulse "Business Value"). */
  area?: boolean;
  /** Draw a glowing dot at the end of the first series for depth. */
  endDot?: boolean;
  /**
   * Play the entrance animation on mount: the first series traces in
   * left→right, the end-dot rides the leading tip, and the area gradient fades
   * in once the line finishes. Honours `prefers-reduced-motion`. Default off, so
   * existing static usages are unchanged.
   */
  animate?: boolean;
  /**
   * A context series of a much larger magnitude (website visits behind the sign-ups): drawn as a faint area BEHIND the
   * series on its own scale, with no axis of its own, so the series keep theirs. Its real values lead the hover
   * readout. It fades in when its points arrive, without replaying the first series' trace. Default none.
   */
  backdrop?: ChartSeries;
};

type Point = { x: number; y: number };

/** A snapshot of the hover readout's content + placement. Held in state so the
 *  card can keep rendering — and animate away from where it was — through its
 *  fade-out after the pointer has already left the plot. */
type TipState = {
  rows: { name: string; color: string; dashed: boolean; value: number }[];
  day: string;
  side: React.CSSProperties;
  above: boolean;
};

/** Catmull-Rom-ish smooth path through the points (matches the source dashboard). */
const smoothPath = (pts: Point[]): string => {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const mx = (p0.x + p1.x) / 2;
    d += ` C ${mx} ${p0.y} ${mx} ${p1.y} ${p1.x} ${p1.y}`;
  }
  return d;
};

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

/** Space (px) between a Y tick label's end and its gridline — the left gutter is
 *  sized so the widest label clears this gap plus a little edge inset. */
const Y_LABEL_GAP = 8;

/** Measure a Y tick label's width at its rendered 10px size, so the chart can
 *  grow its left gutter to fit the widest label (wide/negative money values like
 *  "$-341.3k" would otherwise clip at the SVG edge). Uses a cached offscreen
 *  canvas; falls back to a per-character estimate where canvas isn't available
 *  (SSR / tests). The font stack only needs to approximate the inherited UI font
 *  — the gutter adds an inset margin, so a small measurement drift is absorbed. */
const AXIS_LABEL_FONT = '10px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
let axisMeasureCtx: CanvasRenderingContext2D | null | undefined;
const measureAxisLabel = (text: string): number => {
  if (axisMeasureCtx === undefined) {
    axisMeasureCtx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
  }
  if (!axisMeasureCtx) return text.length * 6;
  axisMeasureCtx.font = AXIS_LABEL_FONT;
  return axisMeasureCtx.measureText(text).width;
};

/** Track the host element's width so the chart is fluid (ResizeObserver). */
const useMeasure = (): readonly [React.RefObject<HTMLDivElement>, number] => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
};

/**
 * Smooth multi-series line chart — a faithful port of the source dashboard's
 * SVG chart. Responsive to its container width; optional left Y axis, an area
 * gradient under the first series and a glowing end-dot. With `animate`, the
 * first series traces itself in on mount. Series colours are supplied by the
 * caller (token values); the axis chrome reads from `COLORS`.
 */
export const LineChart = ({
  series,
  xLabels,
  pointLabels = [],
  height = 130,
  yTicks = 3,
  showY = true,
  formatY,
  area = false,
  endDot = false,
  animate = false,
  backdrop,
}: LineChartProps): React.ReactElement => {
  const [ref, w] = useMeasure();
  const uid = React.useId().replace(/:/g, '');
  const lineRef = React.useRef<SVGPathElement>(null);

  const padR = endDot ? 8 : 6;
  const padT = 8;
  const padB = xLabels.length ? 18 : 6;

  const allVals = series.flatMap((s) => s.points);
  const max = Math.max(1, ...allVals) * 1.08;
  const min = Math.min(0, ...allVals);

  const ticks = Array.from(
    { length: yTicks },
    (_, i) => min + ((max - min) * (yTicks - 1 - i)) / (yTicks - 1),
  );
  const fmt = formatY ?? ((n: number): string => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${Math.round(n)}`));

  // Left gutter sized to the WIDEST formatted Y tick, so wide/negative money
  // labels never clip at the SVG edge — with the prior 34px kept as the floor for
  // short labels. Width = label + the gap to its gridline + a little edge inset.
  const padL = showY
    ? Math.max(34, Math.ceil(Math.max(0, ...ticks.map((t) => measureAxisLabel(fmt(t)))) + Y_LABEL_GAP + 4))
    : 4;
  const innerW = Math.max(0, w - padL - padR);
  const innerH = height - padT - padB;
  const xAt = (i: number, n: number): number => padL + (n <= 1 ? 0 : (i / (n - 1)) * innerW);
  const yAt = (v: number): number => padT + innerH - ((v - min) / (max - min || 1)) * innerH;

  // The backdrop's own scale, from 0 to a little past its peak (it never shares the series' axis).
  const backdropPts = backdrop && backdrop.points.length ? backdrop.points : null;
  const backdropMax = backdropPts ? Math.max(1, ...backdropPts) * 1.08 : 1;
  const yBackdrop = (v: number): number => padT + innerH - (Math.max(0, v) / backdropMax) * innerH;

  // ── Hover readout ──────────────────────────────────────────────────────────
  // Snap to the nearest *data point* (a curve vertex), not to an x-label: a chart
  // can carry more points than labels, so the markers must land on real samples.
  // The tooltip's day heading maps the snapped point back to the closest x-label
  // by proportional position.
  const maxLen = series.reduce((m, s) => Math.max(m, s.points.length), backdropPts ? backdropPts.length : 0);
  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);
  // A touch readout stays *pinned* at the tapped point after the finger lifts
  // (desktop hover, by contrast, clears on pointer-leave). Drives the
  // outside-tap / scroll dismissal effect below.
  const [touchPinned, setTouchPinned] = React.useState(false);
  const svgRef = React.useRef<SVGSVGElement>(null);
  // A touch tap synthesises a click; swallow it so tapping a chart that sits
  // inside a clickable card (the channel sparklines) reveals the readout without
  // also triggering the card. Mouse clicks pass through (desktop nav unchanged).
  const swallowClickRef = React.useRef(false);

  const idxAtClientX = (clientX: number, svg: SVGSVGElement): number => {
    const rect = svg.getBoundingClientRect();
    const frac = (clientX - rect.left - padL) / innerW;
    return Math.max(0, Math.min(maxLen - 1, Math.round(frac * (maxLen - 1))));
  };
  const clearHover = (): void => {
    setHoverIdx(null);
    setTouchPinned(false);
  };

  // Mouse hover, or an active touch/pen scrub (these events only fire while the
  // finger is down): snap the readout to the nearest point.
  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>): void => {
    if (maxLen < 1 || innerW <= 0) return;
    setHoverIdx(idxAtClientX(e.clientX, e.currentTarget));
  };
  // Touch/pen press — a tap, or the start of a scrub. Reveal + pin the readout at
  // the touched point and capture the pointer so the scrub keeps tracking even
  // past the chart's edges. Mouse keeps its hover model (nothing to pin here).
  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>): void => {
    if (e.pointerType === 'mouse') {
      swallowClickRef.current = false;
      return;
    }
    if (maxLen < 1 || innerW <= 0) return;
    swallowClickRef.current = true;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture unsupported / pointer already gone — scrubbing still works */
    }
    setTouchPinned(true);
    setHoverIdx(idxAtClientX(e.clientX, e.currentTarget));
  };
  // Finger lifted: drop the capture but leave the readout pinned (an outside tap
  // or a scroll dismisses it — see the effect below).
  const handlePointerUp = (e: React.PointerEvent<SVGSVGElement>): void => {
    if (e.pointerType === 'mouse') return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };
  // The browser took the gesture over (e.g. a vertical pan → page scroll): drop
  // the readout rather than leaving it stranded mid-scroll.
  const handlePointerCancel = (e: React.PointerEvent<SVGSVGElement>): void => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    clearHover();
  };
  // Mouse leaves the plot → dismiss. (Touch dismissal is the effect below; a
  // captured touch doesn't emit pointer-leave until release anyway.)
  const handlePointerLeave = (e: React.PointerEvent<SVGSVGElement>): void => {
    if (e.pointerType !== 'mouse') return;
    clearHover();
  };
  const handleClick = (e: React.MouseEvent<SVGSVGElement>): void => {
    if (!swallowClickRef.current) return;
    swallowClickRef.current = false;
    e.stopPropagation();
    e.preventDefault();
  };

  // Dismiss a pinned (touch) readout on an outside tap or a scroll. Bound only
  // while pinned, so desktop hover never installs document-level listeners.
  React.useEffect(() => {
    if (!touchPinned) return undefined;
    const dismiss = (): void => {
      setHoverIdx(null);
      setTouchPinned(false);
    };
    const onDocPointerDown = (ev: PointerEvent): void => {
      const el = svgRef.current;
      if (el && ev.target instanceof Node && el.contains(ev.target)) return; // a tap on the chart re-scrubs
      dismiss();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [touchPinned]);

  const hoverRows =
    hoverIdx === null
      ? []
      : series
          .filter((s) => hoverIdx < s.points.length)
          .map((s) => ({ s, v: s.points[hoverIdx], y: yAt(s.points[hoverIdx]) }));
  const hoverBackdrop =
    hoverIdx === null || !backdrop || !backdropPts || hoverIdx >= backdropPts.length
      ? null
      : { s: backdrop, v: backdropPts[hoverIdx], y: yBackdrop(backdropPts[hoverIdx]) };
  const hoverX = hoverIdx === null ? 0 : xAt(hoverIdx, maxLen);
  // Heading source: per-point `pointLabels` win (indexed directly by the hovered
  // sample); otherwise fall back to mapping the point onto the nearest `xLabels`
  // tick by proportional position (a chart can carry more points than labels).
  const hoverDay =
    hoverIdx === null
      ? ''
      : pointLabels.length
        ? pointLabels[Math.min(pointLabels.length - 1, hoverIdx)] ?? ''
        : xLabels.length
          ? xLabels[Math.max(0, Math.min(xLabels.length - 1, Math.round((hoverIdx / Math.max(1, maxLen - 1)) * (xLabels.length - 1))))] ?? ''
          : '';
  // Short charts (the channel sparklines) can't fit the card below the top edge,
  // so anchor it just above the plot; tall charts pin it inside, at the top.
  const tipAbove = height < 100;
  const tipSide: React.CSSProperties = hoverX > w * 0.5 ? { right: w - hoverX + 12 } : { left: hoverX + 12 };

  // ── Tooltip enter/exit choreography ────────────────────────────────────────
  // The card fades + rises in when the pointer enters the plot and fades + drops
  // out when it leaves. To let the exit transition play we keep the card mounted
  // through it (the area's keep-mounted-then-drop pattern): `tipOn` drives the
  // CSS, `retainedTip` holds the last content so the card can animate away from
  // where it was after the live snapshot has already gone. While hovering the
  // live snapshot drives the render and tracks the pointer with no re-animation.
  const tipOpen = hoverIdx !== null && hoverRows.length > 0;
  const liveTip: TipState | null = tipOpen
    ? {
        rows: (hoverBackdrop ? [hoverBackdrop, ...hoverRows] : hoverRows).map(({ s, v }) => ({
          name: s.name,
          color: s.color,
          dashed: !!s.dashed,
          value: v,
        })),
        day: hoverDay,
        side: tipSide,
        above: tipAbove,
      }
    : null;
  const lastTipRef = React.useRef<TipState | null>(null);
  // Refs are safe to write during render; this keeps the last on-screen content
  // so the exit animation starts from the final hovered position.
  if (liveTip) lastTipRef.current = liveTip;

  const [tipOn, setTipOn] = React.useState(false);
  const [retainedTip, setRetainedTip] = React.useState<TipState | null>(null);
  const tipShownRef = React.useRef(false);
  const tipExitTimer = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    if (tipOpen) {
      // Pointer over the plot: cancel any pending drop, let the live snapshot
      // drive the render, then flip ON one frame later so the transition plays.
      window.clearTimeout(tipExitTimer.current);
      tipShownRef.current = true;
      setRetainedTip(null);
      const raf = requestAnimationFrame(() => setTipOn(true));
      return () => cancelAnimationFrame(raf);
    }
    if (tipShownRef.current) {
      // Pointer left: freeze the last content in place, fade + drop it out, then
      // unmount once the transition has finished.
      tipShownRef.current = false;
      setRetainedTip(lastTipRef.current);
      setTipOn(false);
      tipExitTimer.current = window.setTimeout(() => setRetainedTip(null), ANIM.CHART_TIP_EXIT_MS);
    }
    return undefined;
  }, [tipOpen]);

  // Drop a pending exit timer if the chart unmounts mid-animation.
  React.useEffect(() => () => window.clearTimeout(tipExitTimer.current), []);

  // Live content while hovering, the frozen snapshot while fading out.
  const shownTip = liveTip ?? retainedTip;

  // ── Draw-on animation state (first series only) ────────────────────────────
  // `dashOffset` runs 1 → 0 over a path normalised to length 1 (pathLength=1),
  // tracing the line left→right; `lineFade` eases its stroke-opacity 0 → 1 so it
  // materialises softly as it draws; `dot` rides the current tip; `areaFill`
  // fades the gradient in once the line is complete. Inert when `animate` is off.
  const lead = series[0];
  const leadN = lead ? lead.points.length : 0;
  const firstPt: Point | null = lead ? { x: xAt(0, leadN), y: yAt(lead.points[0]) } : null;
  const lastPt: Point | null = lead ? { x: xAt(leadN - 1, leadN), y: yAt(lead.points[leadN - 1]) } : null;
  const animateLine = animate && !!lead && !lead.dashed;

  const [dashOffset, setDashOffset] = React.useState(animateLine ? 1 : 0);
  const [lineFade, setLineFade] = React.useState(animateLine ? 0 : 1);
  const [areaFill, setAreaFill] = React.useState(animateLine ? 0 : 1);
  const [dot, setDot] = React.useState<Point | null>(null);

  // Re-run when the geometry (width) or the data changes.
  const sig = `${w}|${height}|${series.map((s) => s.points.map((p) => Math.round(p)).join(',')).join('|')}`;

  React.useLayoutEffect(() => {
    if (!animateLine || w <= 0 || !lastPt || !firstPt) {
      setDashOffset(0);
      setAreaFill(1);
      setDot(null);
      return;
    }
    const path = lineRef.current;
    if (!path) return;

    const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDashOffset(0);
      setLineFade(1);
      setAreaFill(1);
      setDot(lastPt);
      return;
    }

    const len = path.getTotalLength();
    setDashOffset(1);
    setLineFade(0);
    setAreaFill(0);
    setDot(firstPt);

    let raf = 0;
    let start: number | null = null;
    const { CHART_DRAW_MS: draw, CHART_AREA_FADE_MS: fade, CHART_DRAW_DELAY_MS: delay } = ANIM;
    const tick = (now: number): void => {
      if (start === null) start = now;
      const el = now - start - delay;
      if (el < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      if (el <= draw) {
        const t = el / draw;
        setDashOffset(1 - easeInOutCubic(t));
        setLineFade(easeOutCubic(t));
        const pt = path.getPointAtLength(easeInOutCubic(t) * len);
        setDot({ x: pt.x, y: pt.y });
        raf = requestAnimationFrame(tick);
      } else if (el <= draw + fade) {
        setDashOffset(0);
        setLineFade(1);
        setDot(lastPt);
        setAreaFill(easeOutCubic((el - draw) / fade));
        raf = requestAnimationFrame(tick);
      } else {
        setDashOffset(0);
        setLineFade(1);
        setAreaFill(1);
        setDot(lastPt);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // `sig` already encodes `w` + the series geometry that firstPt/lastPt derive
    // from, so it is the intended trigger — listing those objects would restart
    // the animation every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animateLine, sig]);

  const dotPos = animateLine ? dot : lastPt;

  // The backdrop fades in one frame after its points arrive (it is usually read separately from the series).
  const hasBackdrop = backdropPts !== null;
  const [backdropOn, setBackdropOn] = React.useState(false);
  React.useEffect(() => {
    if (!hasBackdrop) {
      setBackdropOn(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setBackdropOn(true));
    return () => cancelAnimationFrame(raf);
  }, [hasBackdrop]);

  return (
    <Wrap ref={ref}>
      {w > 0 ? (
        <Svg
          ref={svgRef}
          width={w}
          height={height}
          viewBox={`0 0 ${w} ${height}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onPointerLeave={handlePointerLeave}
          onClick={handleClick}
        >
          {/* unpainted capture layer so the whole plot area is hit-testable
              (fill="none" + pointer-events:all reports hits without a colour) */}
          <rect x={0} y={0} width={w} height={height} fill="none" pointerEvents="all" />

          {/* faint gridlines + y labels */}
          {showY
            ? ticks.map((t, i) => {
                const y = yAt(t);
                return (
                  <g key={`y-${i}`}>
                    <line x1={padL} y1={y} x2={w - padR} y2={y} stroke={COLORS.chartGridLine} strokeOpacity={0.05} strokeWidth={1} />
                    <text x={padL - Y_LABEL_GAP} y={y + 3.5} textAnchor="end" fontSize={10} fill={COLORS.textSubtle}>
                      {fmt(t)}
                    </text>
                  </g>
                );
              })
            : null}

          {/* x labels — anchor the edge labels inward so they don't clip */}
          {xLabels.map((lab, i) => (
            <text
              key={`x-${i}`}
              x={xAt(i, xLabels.length)}
              y={height - 3}
              textAnchor={i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'}
              fontSize={10}
              fill={COLORS.textSubtle}
            >
              {lab}
            </text>
          ))}

          {/* the backdrop: a faint area on its own scale, behind everything the series draw */}
          {backdrop && backdropPts
            ? (() => {
                const pts = backdropPts.map((v, i) => ({ x: xAt(i, backdropPts.length), y: yBackdrop(v) }));
                const baseY = padT + innerH;
                const gid = `lc-backdrop-${uid}`;
                return (
                  <g
                    opacity={backdropOn ? 1 : 0}
                    style={{ transition: `opacity ${ANIM.CHART_AREA_FADE_MS}ms ease`, pointerEvents: 'none' }}
                  >
                    <defs>
                      <linearGradient id={gid} x1="0" y1={padT} x2="0" y2={baseY} gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor={backdrop.color} stopOpacity={0.16} />
                        <stop offset="100%" stopColor={backdrop.color} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <path
                      d={`${smoothPath(pts)} L ${pts[pts.length - 1].x} ${baseY} L ${pts[0].x} ${baseY} Z`}
                      fill={`url(#${gid})`}
                      stroke="none"
                    />
                    <path
                      d={smoothPath(pts)}
                      fill="none"
                      stroke={backdrop.color}
                      strokeOpacity={0.35}
                      strokeWidth={1.5}
                    />
                  </g>
                );
              })()
            : null}

          {/* gradient area fill under the first series (fades in after the trace) */}
          {area && lead
            ? (() => {
                const pts = lead.points.map((v, i) => ({ x: xAt(i, lead.points.length), y: yAt(v) }));
                const baseY = padT + innerH;
                const gid = `lc-area-${uid}`;
                return (
                  <g opacity={areaFill}>
                    <defs>
                      <linearGradient id={gid} x1="0" y1={padT} x2="0" y2={baseY} gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor={lead.color} stopOpacity={0.34} />
                        <stop offset="52%" stopColor={lead.color} stopOpacity={0.08} />
                        <stop offset="100%" stopColor={lead.color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <path
                      d={`${smoothPath(pts)} L ${pts[pts.length - 1].x} ${baseY} L ${pts[0].x} ${baseY} Z`}
                      fill={`url(#${gid})`}
                      stroke="none"
                    />
                  </g>
                );
              })()
            : null}

          {/* series lines (the first one traces in when `animate`) */}
          {series.map((s, si) => {
            const pts = s.points.map((v, i) => ({ x: xAt(i, s.points.length), y: yAt(v) }));
            const isLead = si === 0;
            const traced = animateLine && isLead;
            return (
              <path
                key={s.name}
                ref={isLead ? lineRef : undefined}
                d={smoothPath(pts)}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeOpacity={traced ? lineFade : undefined}
                pathLength={traced ? 1 : undefined}
                strokeDasharray={traced ? '1 1' : s.dashed ? '4 4' : undefined}
                strokeDashoffset={traced ? dashOffset : undefined}
              />
            );
          })}

          {/* glowing end-dot — rides the leading tip, fading in with the line */}
          {endDot && lead && dotPos
            ? (
              <g opacity={animateLine ? lineFade : 1}>
                <circle cx={dotPos.x} cy={dotPos.y} r={7} fill={lead.color} opacity={0.18} />
                <circle cx={dotPos.x} cy={dotPos.y} r={4} fill={COLORS.chartGridLine} />
                <circle cx={dotPos.x} cy={dotPos.y} r={2.5} fill={lead.color} />
              </g>
            )
            : null}

          {/* hover crosshair + a marker riding each series at the snapped point */}
          {hoverIdx !== null && hoverRows.length ? (
            <g style={{ pointerEvents: 'none' }}>
              <line
                x1={hoverX}
                y1={padT}
                x2={hoverX}
                y2={padT + innerH}
                stroke={COLORS.chartGridLine}
                strokeOpacity={0.18}
                strokeWidth={1}
              />
              {hoverBackdrop ? (
                <circle
                  cx={hoverX}
                  cy={hoverBackdrop.y}
                  r={3}
                  fill={hoverBackdrop.s.color}
                  fillOpacity={0.6}
                  stroke={COLORS.chartGridLine}
                  strokeWidth={1}
                />
              ) : null}
              {hoverRows.map(({ s, y }) => (
                <g key={s.name}>
                  <circle cx={hoverX} cy={y} r={6} fill={s.color} opacity={0.18} />
                  <circle cx={hoverX} cy={y} r={3.5} fill={s.color} stroke={COLORS.chartGridLine} strokeWidth={1.5} />
                </g>
              ))}
            </g>
          ) : null}
        </Svg>
      ) : null}

      {/* floating readout — every series' value at the hovered point, colour-coded.
          Kept mounted through its fade-out so it animates away (see shownTip). */}
      {shownTip ? (
        <Tooltip
          $on={tipOn}
          style={shownTip.above ? { ...shownTip.side, bottom: height - padT } : { ...shownTip.side, top: padT }}
        >
          {shownTip.day ? <TipDay text={shownTip.day} tag="span" /> : null}
          <TipRows>
            {shownTip.rows.map((r) => (
              <TipRow key={r.name}>
                <TipSwatch $color={r.color} $dashed={r.dashed} aria-hidden="true" />
                <TipName text={r.name} tag="span" />
                <TipValue>{fmt(r.value)}</TipValue>
              </TipRow>
            ))}
          </TipRows>
        </Tooltip>
      ) : null}
    </Wrap>
  );
};

export default LineChart;
